import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Heart, MapPin, Sparkles, HandHeart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_CATEGORIES = [
  { id: 'detox', label: 'Detox / Reset', icon: Sparkles },
  { id: 'health', label: 'Health', icon: Heart },
  { id: 'spiritual', label: 'Spiritual', icon: HandHeart },
  { id: 'mental_clarity', label: 'Mental Clarity', icon: Sparkles },
  { id: 'solidarity', label: 'Solidarity', icon: Heart },
  { id: 'other', label: 'Other', icon: Sparkles },
];

const CommitmentModal = ({ 
  isOpen, 
  onClose, 
  selectedDate, 
  existingCommitments = [],
  onSuccess 
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    display_name: user?.display_name || '',
    reason: '',
    location: '',
    reason_categories: [],
    charity_name: '',
    is_anonymous: false,
  });

  // Get suggested charity from existing commitments
  const suggestedCharity = existingCommitments.find(c => c.charity_name)?.charity_name;

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      reason_categories: prev.reason_categories.includes(categoryId)
        ? prev.reason_categories.filter(c => c !== categoryId)
        : [...prev.reason_categories, categoryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.reason.trim()) {
      toast.error('Please share your reason for fasting');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/commitments`, {
        ...formData,
        date: selectedDate,
      });
      toast.success('Your fasting commitment has been recorded');
      onSuccess?.();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create commitment';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="commitment-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Commit to Fast
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {formatDisplayDate(selectedDate)}
          </DialogDescription>
        </DialogHeader>

        {existingCommitments.length > 0 && (
          <div className="bg-sage-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-sage-700 font-medium">
              {existingCommitments.length} {existingCommitments.length === 1 ? 'person is' : 'people are'} already fasting on this day
            </p>
            {suggestedCharity && (
              <p className="text-sm text-muted-foreground mt-1">
                Some are supporting: {suggestedCharity}
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              value={formData.display_name}
              onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              placeholder="Your name"
              disabled={formData.is_anonymous}
              data-testid="commitment-display-name"
            />
            <div className="flex items-center space-x-2 mt-2">
              <Checkbox
                id="anonymous"
                checked={formData.is_anonymous}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_anonymous: checked }))}
                data-testid="commitment-anonymous"
              />
              <Label htmlFor="anonymous" className="text-sm text-muted-foreground cursor-pointer">
                Fast anonymously
              </Label>
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Why are you fasting? *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Share your intention or motivation..."
              rows={3}
              data-testid="commitment-reason"
            />
          </div>

          {/* Reason Categories */}
          <div className="space-y-2">
            <Label>Categories (optional)</Label>
            <div className="flex flex-wrap gap-2">
              {REASON_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCategoryToggle(cat.id)}
                  data-testid={`commitment-category-${cat.id}`}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all
                    ${formData.reason_categories.includes(cat.id)
                      ? 'bg-primary text-white'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }
                  `}
                >
                  <cat.icon className="h-3.5 w-3.5" />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location (optional)</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Glasgow, UK"
                className="pl-10"
                data-testid="commitment-location"
              />
            </div>
          </div>

          {/* Charity */}
          <div className="space-y-2">
            <Label htmlFor="charity">Supporting a cause (optional)</Label>
            <Input
              id="charity"
              value={formData.charity_name}
              onChange={(e) => setFormData(prev => ({ ...prev, charity_name: e.target.value }))}
              placeholder={suggestedCharity || "e.g., Medical Aid for Palestine"}
              data-testid="commitment-charity"
            />
            {suggestedCharity && !formData.charity_name && (
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, charity_name: suggestedCharity }))}
                className="text-sm text-primary hover:underline"
                data-testid="commitment-use-suggested-charity"
              >
                Use suggested: {suggestedCharity}
              </button>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Consider donating what you might have spent on food today, independently.
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="commitment-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-full"
              data-testid="commitment-submit"
            >
              {loading ? 'Committing...' : 'Commit to Fast'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CommitmentModal;
